import { ColDef } from 'ag-grid-community';
import { TachoVersion } from 'tacho-models';
import { TachoReleaseInformationModel } from '../../../../core/domainModels/versions/TachoInstallerModel';
import {
    assignColDefsDefaults,
    COLUMN_WIDTH,
    compareDateFilter,
    compareFilterList,
    dateSortComparator,
    fieldName,
    filterMenuTab,
} from '../../../../core/helpers/aggrid';
import { TachoColumnState } from '../../../../core/domainModels/displayPreferences/tachoColumnState';
import { dateToStringFormat } from '../../../../core/helpers/moment';
import { ColumnTooltip } from '../../../components/TachoTooltip/columnTooltip';
import {
    copyRendererIcon,
    downloadRendererIcon,
    eyeRendererIcon,
} from '../../../components/AgGrid/Renderers/TableRemoveButton/icons';
import { SetFilter } from '../../../components/AgGrid/Filters/SetFilter';
import { formatToYesOrNo } from '../../../components/AgGrid/services';

export const versionsTableDefaultColumnsDefs: ColDef = {
    sortable: true,
    resizable: true,
    filter: SetFilter,
    filterParams: {
        comparator: compareFilterList,
    },
    tooltipComponent: ColumnTooltip,
    sortingOrder: ['asc', 'desc', null],
    menuTabs: filterMenuTab,
};

export const versionsTableColumnsDefinitions = (
    formatMessage: ({ id }: { id: string }) => string,
    versionDetailsModalOpen: (isModalOpen: boolean, data?: TachoReleaseInformationModel) => void,
    readOnly: boolean | undefined,
    defColumnState: Array<TachoColumnState>,
): Array<ColDef> => {
    const columns: Array<ColDef> = [
        {
            colId: '0',
            headerName: '',
            field: 'selected',

            headerCheckboxSelection: true,
            checkboxSelection: true,
            maxWidth: 40,
            minWidth: 40,
        },
        {
            colId: fieldName<TachoReleaseInformationModel>('name'),
            headerName: formatMessage({ id: 'versionsName' }),
            headerTooltip: formatMessage({ id: 'versionsName' }),
            field: fieldName<TachoReleaseInformationModel>('name'),
            initialWidth: COLUMN_WIDTH.XXL,
        },
        {
            colId: fieldName<TachoReleaseInformationModel>('downloadUrl'),
            headerName: formatMessage({ id: 'versionsDownload' }),
            headerTooltip: formatMessage({ id: 'versionsDownload' }),
            field: fieldName<TachoReleaseInformationModel>('downloadUrl'),
            maxWidth: COLUMN_WIDTH.SM,
            minWidth: COLUMN_WIDTH.SM,
            cellRenderer: 'tableButtonRenderer',
            cellRendererParams: {
                onClick: (data: TachoReleaseInformationModel) => {
                    const link = document.createElement('a');
                    link.href = data.downloadUrl;
                    link.click();
                },
                disabled: () => {},
                icon: downloadRendererIcon,
            },
            filter: false,
            sortable: false,
        },
        {
            colId: fieldName<TachoReleaseInformationModel>('downloadUrl'),
            headerName: formatMessage({ id: 'versionsCopyLink' }),
            headerTooltip: formatMessage({ id: 'versionsCopyLink' }),
            field: fieldName<TachoReleaseInformationModel>('downloadUrl'),
            maxWidth: COLUMN_WIDTH.SM,
            minWidth: COLUMN_WIDTH.SM,
            cellRenderer: 'tableButtonRenderer',
            cellRendererParams: {
                onClick: (data: TachoReleaseInformationModel) => {
                    navigator.clipboard.writeText(data.downloadUrl);
                },
                disabled: () => {},
                icon: copyRendererIcon,
            },
            filter: false,
            sortable: false,
        },
        {
            colId: fieldName<TachoReleaseInformationModel>('version'),
            headerName: formatMessage({ id: 'versionsVersion' }),
            headerTooltip: formatMessage({ id: 'versionsVersion' }),
            field: fieldName<TachoReleaseInformationModel>('version'),
            initialWidth: COLUMN_WIDTH.LG,
            valueGetter(params) {
                const version: TachoVersion = params.data.version;
                if (version) {
                    return `${version.major}.${version.minor}.${version.patch}`;
                }
                return '';
            },
            filterValueGetter(params) {
                const version: TachoVersion = params.data.version;
                if (version) {
                    return `${version.major}.${version.minor}.${version.patch}`;
                }
                return '';
            },
        },
        {
            colId: fieldName<TachoReleaseInformationModel>('releaseType'),
            headerName: formatMessage({ id: 'versionsReleaseType' }),
            headerTooltip: formatMessage({ id: 'versionsReleaseType' }),
            field: fieldName<TachoReleaseInformationModel>('releaseType'),
            initialWidth: COLUMN_WIDTH.XL,
            valueFormatter(params) {
                return params.value ? formatMessage({ id: params.value }) : '-';
            },
            filterValueGetter(params) {
                return params.data.releaseType ? formatMessage({ id: params.data.releaseType }) : '-';
            },
        },
        {
            colId: fieldName<TachoReleaseInformationModel>('component'),
            headerName: formatMessage({ id: 'versionsComponent' }),
            headerTooltip: formatMessage({ id: 'versionsComponent' }),
            field: fieldName<TachoReleaseInformationModel>('component'),
            initialWidth: COLUMN_WIDTH.LG,
            valueFormatter(params) {
                return params.value || '-';
            },
        },
        {
            colId: fieldName<TachoReleaseInformationModel>('releaseDate'),
            headerName: formatMessage({ id: 'versionsReleaseDate' }),
            headerTooltip: formatMessage({ id: 'versionsReleaseDate' }),
            field: fieldName<TachoReleaseInformationModel>('releaseDate'),
            initialWidth: COLUMN_WIDTH.LG,
            comparator: dateSortComparator,
            valueFormatter(params) {
                return dateToStringFormat(params.value);
            },
            filterValueGetter(params) {
                return dateToStringFormat(params.data.releaseDate);
            },
            filterParams: {
                comparator: compareDateFilter,
            },
        },
        {
            colId: fieldName<TachoReleaseInformationModel>('isBeta'),
            headerName: formatMessage({ id: 'versionsBeta' }),
            headerTooltip: formatMessage({ id: 'versionsBeta' }),
            field: fieldName<TachoReleaseInformationModel>('isBeta'),
            initialWidth: 115,
            valueGetter: params => formatToYesOrNo(params.data?.isBeta, formatMessage),
            filterValueGetter: params => formatToYesOrNo(params.data?.isBeta, formatMessage),
        },
        {
            colId: fieldName<TachoReleaseInformationModel>('patchNotes'),
            headerName: formatMessage({ id: 'versionsPatchNotes' }),
            headerTooltip: formatMessage({ id: 'versionsPatchNotes' }),
            field: fieldName<TachoReleaseInformationModel>('patchNotes'),
            initialWidth: COLUMN_WIDTH.XS,

            cellRenderer: 'tableButtonRenderer',
            cellRendererParams: (params: any) => {
                return {
                    onClick: () => versionDetailsModalOpen(true, params.data),
                    disabled: () => {},
                    icon: eyeRendererIcon,
                };
            },
            filter: false,
            sortable: false,
            flex: 1,
        },
    ];
    assignColDefsDefaults(columns, defColumnState, true);
    return columns;
};
